import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/adminApi";
import { fmtINR, fmtRelative, fmtDate } from "../lib/format";
import { Search, ChevronDown, Loader2, Building2, Eye, ArrowUpDown, UserCog, Ban, CheckCircle2, Plus } from "lucide-react";
import ImpersonateModal from "../components/ImpersonateModal";
import PlanChangeModal from "../components/PlanChangeModal";

const STATUS_BADGE = {
  active: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Active" },
  suspended: { bg: "bg-red-50", text: "text-red-700", label: "Suspended" },
};

const PLAN_BADGE = {
  free: { bg: "bg-slate-100", text: "text-slate-600" },
  trial: { bg: "bg-amber-50", text: "text-amber-700" },
  standard: { bg: "bg-blue-50", text: "text-blue-700" },
  pro: { bg: "bg-violet-50", text: "text-violet-700" },
  premium: { bg: "bg-[#7C5CFF]/10", text: "text-[#7C5CFF]" },
};

const COUNTRY_FLAG = { IN: "🇮🇳", AE: "🇦🇪", SA: "🇸🇦", QA: "🇶🇦", BH: "🇧🇭", KW: "🇰🇼", OM: "🇴🇲", OTHER: "🌐" };

const COUNTRY_TABS = [
  { code: "", label: "All", flag: "🌐" },
  { code: "IN", label: "India", flag: "🇮🇳" },
  { code: "AE", label: "UAE", flag: "🇦🇪" },
  { code: "SA", label: "Saudi", flag: "🇸🇦" },
  { code: "QA", label: "Qatar", flag: "🇶🇦" },
  { code: "BH", label: "Bahrain", flag: "🇧🇭" },
  { code: "KW", label: "Kuwait", flag: "🇰🇼" },
  { code: "OM", label: "Oman", flag: "🇴🇲" },
];

export default function TenantsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_COUNTRIES = new Set(["IN","AE","SA","QA","BH","KW","OM","OTHER"]);
  const initCountry = searchParams.get("country")?.toUpperCase() || "";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState(VALID_COUNTRIES.has(initCountry) ? initCountry : "");
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 25;

  // Modal states
  const [impTarget, setImpTarget] = useState(null);
  const [planTarget, setPlanTarget] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, sort: sortField, order: sortOrder };
      if (search.trim()) params.search = search.trim();
      if (planFilter) params.plan = planFilter;
      if (statusFilter) params.status = statusFilter;
      if (countryFilter) params.country = countryFilter;
      const res = await api.get("/tenants", { params });
      setRows(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [page, sortField, sortOrder, search, planFilter, statusFilter, countryFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (field) => {
    if (sortField === field) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortOrder("desc"); }
    setPage(1);
  };

  const suspendTenant = async (id, name) => {
    const reason = window.prompt(`Suspend "${name}"? Enter reason:`);
    if (!reason) return;
    try {
      await api.post(`/tenants/${id}/suspend`, { reason });
      load();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
    setActionMenu(null);
  };

  const activateTenant = async (id) => {
    if (!window.confirm("Activate this tenant?")) return;
    try { await api.post(`/tenants/${id}/activate`); load(); } catch (err) { alert(err.response?.data?.detail || "Failed"); }
    setActionMenu(null);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto glass-animate" data-testid="tenants-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-extrabold text-[#0B0F1A] tracking-tight">Tenants</h1>
        <button data-testid="onboard-tenant-btn" onClick={() => navigate("/admin/tenants/new")}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-white text-xs font-semibold btn-primary-glass">
          <Plus className="w-3.5 h-3.5" />Onboard Tenant
        </button>
      </div>
      <p className="text-sm text-[#5B617A] mb-4">{total} total workspace{total !== 1 ? "s" : ""} on the platform</p>

      {/* Country tabs */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-1" data-testid="country-tabs">
        {COUNTRY_TABS.map((tab) => (
          <button
            key={tab.code}
            data-testid={`country-tab-${tab.code || "all"}`}
            onClick={() => { setCountryFilter(tab.code); setPage(1); setSearchParams(prev => { if (tab.code) prev.set("country", tab.code); else prev.delete("country"); return prev; }); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all border ${
              countryFilter === tab.code
                ? "bg-[#7C5CFF]/10 border-[#7C5CFF]/30 text-[#7C5CFF]"
                : "bg-white/50 border-[#7C5CFF]/[0.06] text-[#5B617A] hover:bg-[#7C5CFF]/[0.04] hover:text-[#0B0F1A]"
            }`}
          >
            <span className="text-sm">{tab.flag}</span> {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2 h-9 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 flex-1 min-w-[200px] max-w-sm backdrop-blur-sm">
          <Search className="w-4 h-4 text-[#5B617A] shrink-0" />
          <input
            data-testid="tenants-search"
            className="flex-1 bg-transparent text-sm text-[#0B0F1A] placeholder:text-[#5B617A]/50 focus:outline-none"
            placeholder="Search by name or owner email…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
        </div>
        <select
          data-testid="tenants-plan-filter"
          value={planFilter}
          onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}
          className="h-9 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm text-[#0B0F1A] focus:outline-none focus:border-[#7C5CFF] backdrop-blur-sm"
        >
          <option value="">All plans</option>
          <option value="free">Free</option>
          <option value="trial">Trial</option>
          <option value="standard">Standard</option>
          <option value="pro">Pro</option>
          <option value="premium">Premium</option>
        </select>
        <select
          data-testid="tenants-status-filter"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="h-9 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm text-[#0B0F1A] focus:outline-none focus:border-[#7C5CFF] backdrop-blur-sm"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {/* Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tenants-table">
            <thead>
              <tr className="bg-white/40 border-b border-[#7C5CFF]/[0.08] text-left">
                <SortHeader label="Tenant" field="name" current={sortField} order={sortOrder} onSort={toggleSort} />
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider w-[72px]">Country</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">GSTIN</th>
                <SortHeader label="Plan" field="plan" current={sortField} order={sortOrder} onSort={toggleSort} />
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Status</th>
                <SortHeader label="Members" field="active_member_count" current={sortField} order={sortOrder} onSort={toggleSort} />
                <SortHeader label="AI Calls (30d)" field="ai_calls_30d" current={sortField} order={sortOrder} onSort={toggleSort} />
                <SortHeader label="Sales Vol." field="total_sales_volume" current={sortField} order={sortOrder} onSort={toggleSort} />
                <SortHeader label="Last Activity" field="last_activity_at" current={sortField} order={sortOrder} onSort={toggleSort} />
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#7C5CFF] mx-auto" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12 text-[#5B617A]">No tenants found.</td></tr>
              ) : rows.map((r) => {
                const st = STATUS_BADGE[r.status] || STATUS_BADGE.active;
                const pl = PLAN_BADGE[r.effective_plan] || PLAN_BADGE.free;
                return (
                  <tr key={r.tenant_id} className="border-b border-[#7C5CFF]/[0.04] glass-row-hover transition-colors" data-testid={`tenant-row-${r.tenant_id}`}>
                    <td className="px-3 py-3">
                      <p className="font-semibold text-[#0B0F1A] truncate max-w-[180px]">{r.name}</p>
                      <p className="text-[10px] text-[#5B617A] truncate">{r.owner_email || "—"}</p>
                    </td>
                    <td className="px-3 py-3 text-xs" data-testid={`country-${r.tenant_id}`}>
                      {r.country_code
                        ? <span className="font-semibold text-[#0B0F1A]">{COUNTRY_FLAG[r.country_code] || "🌐"} {r.country_code}</span>
                        : <span className="text-[#5B617A]/50">— Unset</span>}
                    </td>
                    <td className="px-3 py-3 text-xs text-[#5B617A] font-mono" data-testid={`gstin-${r.tenant_id}`}>{r.gstin || "—"}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${pl.bg} ${pl.text}`}>
                        {r.effective_plan}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${st.bg} ${st.text}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-[#0B0F1A] tabular-nums" style={{ fontVariantNumeric: "tabular-nums" }}>{r.active_member_count}</td>
                    <td className="px-3 py-3 text-[#0B0F1A] tabular-nums" style={{ fontVariantNumeric: "tabular-nums" }}>{r.ai_calls_30d}</td>
                    <td className="px-3 py-3 text-[#0B0F1A] tabular-nums" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINR(r.total_sales_volume)}</td>
                    <td className="px-3 py-3 text-[10px] text-[#5B617A]">{fmtRelative(r.last_activity_at)}</td>
                    <td className="px-3 py-3 relative">
                      <button
                        data-testid={`actions-${r.tenant_id}`}
                        onClick={(e) => { e.stopPropagation(); setActionMenu(actionMenu === r.tenant_id ? null : r.tenant_id); }}
                        className="p-1.5 rounded-lg hover:bg-[#7C5CFF]/[0.06] transition-colors"
                      >
                        <ChevronDown className="w-4 h-4 text-[#5B617A]" />
                      </button>
                      {actionMenu === r.tenant_id && (
                        <ActionDropdown
                          tenant={r}
                          onClose={() => setActionMenu(null)}
                          onView={() => { navigate(`/admin/tenants/${r.tenant_id}`); setActionMenu(null); }}
                          onChangePlan={() => { setPlanTarget(r); setActionMenu(null); }}
                          onImpersonate={() => { setImpTarget(r); setActionMenu(null); }}
                          onSuspend={() => suspendTenant(r.tenant_id, r.name)}
                          onActivate={() => activateTenant(r.tenant_id)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#7C5CFF]/[0.06]">
            <p className="text-xs text-[#5B617A]">Page {page} of {totalPages}</p>
            <div className="flex gap-1.5">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page"
                className="px-3 py-1 rounded-lg text-xs font-semibold text-[#0B0F1A] bg-white/60 border border-[#7C5CFF]/[0.12] hover:bg-[#7C5CFF]/[0.06] disabled:opacity-40">Prev</button>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="next-page"
                className="px-3 py-1 rounded-lg text-xs font-semibold text-[#0B0F1A] bg-white/60 border border-[#7C5CFF]/[0.12] hover:bg-[#7C5CFF]/[0.06] disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {impTarget && <ImpersonateModal tenant={impTarget} onClose={() => setImpTarget(null)} />}
      {planTarget && <PlanChangeModal tenant={planTarget} onClose={() => { setPlanTarget(null); load(); }} />}
    </div>
  );
}

function SortHeader({ label, field, current, order, onSort }) {
  const active = current === field;
  return (
    <th className="px-3 py-2.5 cursor-pointer select-none" onClick={() => onSort(field)}>
      <div className="flex items-center gap-1 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${active ? "text-[#7C5CFF]" : "text-[#5B617A]/30"}`} />
      </div>
    </th>
  );
}

function ActionDropdown({ tenant, onClose, onView, onChangePlan, onImpersonate, onSuspend, onActivate }) {
  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div className="absolute right-0 top-full mt-1 glass-dropdown z-40 min-w-[160px] py-1" data-testid="actions-dropdown">
        <DropItem icon={Eye} label="View detail" onClick={onView} testId={`view-${tenant.tenant_id}`} />
        <DropItem icon={ArrowUpDown} label="Change plan" onClick={onChangePlan} testId={`plan-${tenant.tenant_id}`} />
        <DropItem icon={UserCog} label="Impersonate" onClick={onImpersonate} testId={`imp-${tenant.tenant_id}`} disabled={tenant.status === "suspended"} />
        {tenant.status === "active" ? (
          <DropItem icon={Ban} label="Suspend" onClick={onSuspend} testId={`suspend-${tenant.tenant_id}`} danger />
        ) : (
          <DropItem icon={CheckCircle2} label="Activate" onClick={onActivate} testId={`activate-${tenant.tenant_id}`} success />
        )}
      </div>
    </>
  );
}

function DropItem({ icon: Icon, label, onClick, testId, danger, success, disabled }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#7C5CFF]/[0.06] transition-colors disabled:opacity-40 disabled:pointer-events-none
        ${danger ? "text-red-600" : success ? "text-emerald-600" : "text-[#0B0F1A]"}`}
    >
      <Icon className="w-3.5 h-3.5" />{label}
    </button>
  );
}
